import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import { getBookById } from '../features/books/bookSlice';
import Skeleton from '../components/Skeleton';
import { Helmet } from 'react-helmet-async';
import { getImageUrl } from '../utils/imageUrl';
import PDFModal from '../components/PDFModal';

const BookDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentBook, isLoading } = useSelector((state) => state.books);
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getBookById(id));
  }, [dispatch, id]);

  const toggleSynopsis = () => {
    setShowFullSynopsis(!showFullSynopsis);
  };

  if (isLoading) {
    return (
      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <Skeleton className="h-96 w-72 mx-auto mb-8 rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-12 w-3/4 rounded-xl" />
              <Skeleton className="h-6 w-1/2 rounded-xl" />
              <Skeleton className="h-6 w-2/3 rounded-xl" />
              <div className="pt-4">
                <Skeleton className="h-6 w-full rounded-lg mb-2" />
                <Skeleton className="h-6 w-full rounded-lg mb-2" />
                <Skeleton className="h-6 w-3/4 rounded-lg" />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!currentBook) {
    return (
      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="text-center py-20">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Buku tidak ditemukan</h3>
            <Link to="/books" className="text-dark-blue-700 font-medium hover:text-dark-blue-800">
              Kembali ke koleksi buku
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const coverUrl = getImageUrl(currentBook.cover);

  return (
    <div className="py-12">
      {currentBook && (
        <Helmet>
          <title>{currentBook.title} | Penerbit Literasiologi</title>
          <meta name="description" content={currentBook.synopsis.substring(0, 200)} />
          <meta property="og:title" content={currentBook.title} />
          <meta property="og:description" content={currentBook.synopsis.substring(0, 200)} />
          <meta property="og:image" content={coverUrl} />
          <meta property="og:type" content="book" />
          <meta name="twitter:title" content={currentBook.title} />
          <meta name="twitter:description" content={currentBook.synopsis.substring(0, 200)} />
          <meta name="twitter:image" content={coverUrl} />
        </Helmet>
      )}
      <section className="container mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-dark-blue-700 transition-colors">Beranda</Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <Link to="/books" className="hover:text-dark-blue-700 transition-colors">Buku</Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </li>
            {currentBook.category && (
              <>
                <li>
                  <Link
                    to={`/books/category/${currentBook.category.toLowerCase().replace(/\s+/g, '-')}`}
                    className="hover:text-dark-blue-700 transition-colors"
                  >
                    {currentBook.category}
                  </Link>
                </li>
                <li>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </li>
              </>
            )}
            <li className="text-gray-900 font-medium truncate max-w-[200px]">
              {currentBook.title}
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt={currentBook.title}
                  className="w-full aspect-[3/4] object-cover rounded-xl shadow-lg mb-4"
                />
              ) : (
                <div className="w-full aspect-[3/4] bg-gradient-to-br from-dark-blue-100 to-dark-blue-200 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-24 h-24 text-dark-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}
              {currentBook.demoPdf && (
                <button
                  onClick={() => setIsPdfModalOpen(true)}
                  className="w-full py-4 bg-dark-blue-700 text-white text-lg font-medium rounded-xl hover:bg-dark-blue-800 transition-colors"
                >
                  Baca Buku
                </button>
              )}
            </div>

            <div className="lg:col-span-2">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-4 py-2 bg-dark-blue-50 text-dark-blue-700 rounded-full text-sm font-medium">
                    {currentBook.category}
                  </span>
                  <span className="text-gray-500">{currentBook.publishedYear}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {currentBook.title}
                </h1>
                <p className="text-xl text-gray-600 mb-2">
                  <span className="font-semibold">Penulis:</span> {currentBook.author}
                </p>
                <p className="text-gray-600 mb-2">
                  <span className="font-semibold">Penerbit:</span> {currentBook.publisher}
                </p>
                <p className="text-gray-600 mb-6">
                  <span className="font-semibold">ISBN:</span> {currentBook.isbn}
                </p>
                <p className="text-3xl font-bold text-dark-blue-700 mb-8">
                  Rp {currentBook.price?.toLocaleString('id-ID') || 0}
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Sinopsis</h2>
                <div className="text-gray-600 leading-relaxed text-lg prose-content">
                  {showFullSynopsis ? (
                    <div dangerouslySetInnerHTML={{ __html: currentBook.synopsis }} />
                  ) : (
                    <>
                      <div dangerouslySetInnerHTML={{ __html: currentBook.synopsis.slice(0, 400) }} />
                      {currentBook.synopsis.length > 400 && '...'}
                    </>
                  )}
                </div>
                {currentBook.synopsis.length > 400 && (
                  <button
                    onClick={toggleSynopsis}
                    className="mt-4 text-dark-blue-700 font-medium hover:text-dark-blue-800 transition-colors"
                  >
                    {showFullSynopsis ? 'Tampilkan Lebih Sedikit' : 'Baca Selengkapnya'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {currentBook && (
        <PDFModal
          isOpen={isPdfModalOpen}
          onClose={() => setIsPdfModalOpen(false)}
          pdfUrl={getImageUrl(currentBook.demoPdf)}
          title={currentBook.title}
        />
      )}
    </div>
  );
};

export default BookDetail;
